import React, { useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { AddIcon, SearchIcon } from "@infotech/uikit-icons"
import Input from "@infotech/uikit-input"
import DataGrid from "@infotech/uikit-datagrid"
import { Box } from "@infotech/uikit-grid"
import { PrimaryButton } from "@infotech/uikit-button"
import { P } from "@infotech/uikit-text"
import {
  RESOURCE_TYPE_CREATE,
  RESOURCE_TYPE_UPDATE,
} from "src/constants/knownPermissions"
import { hasPermission } from "src/engine/modules/currentUser"

import { clear } from "src/engine/modules/resourceTypeFields/actions/creators/clear"
import { remove } from "src/engine/modules/resourceTypeFields/actions/creators/remove"
import ModalConfirm from "src/components/modals/ModalConfirm"

import map from "lodash/map"
import get from "lodash/get"
import isEmpty from "lodash/isEmpty"

import cn from "classnames"
import useFields from "./hook/useFields"

import CustomField from "../CustomField"
import styles from "./styles.css"

const getTestAttribute = name => `planner-resource-type-custom-field-${name}`

const columns = [
  {
    Header: `№`,
    accessor: `index`,
    sortable: true,
    width: 56,
  },
  {
    Header: `Наименование`,
    accessor: `name`,
    sortable: true,
  },
  {
    Header: `Описание`,
    accessor: `label`,
    sortable: true,
  },
  {
    Header: `Тип поля`,
    accessor: `type`,
    sortable: true,
  },
  {
    Header: `Обязательное поле`,
    accessor: `required`,
    sortable: true,
    Cell: ({ original: { required } }) => <div>{required ? `Да` : `Нет`}</div>,
  },
]
const stringifyOrderBy = orderBy => {
  return orderBy
    ? map(orderBy, ({ id, desc }) => `${id} ${desc ? `desc` : `asc`}`).join(` `)
    : undefined
}

const GridFields = ({
  typeId,
  areMenuCaptionsVisible,
  remove,
  clear,
  disableCreate,
  disableEdit,
}) => {
  // confirm modal for delete field
  const [deletedField, toggleDeletedField] = useState(false)

  // field CRUD modal trigger
  const [modalData, setModalData] = useState({})

  // grid props
  const [pageParams, setPageParams] = useState({
    page: 1,
    pageSize: 20,
    sortBy: null,
    query: null,
  })
  const { page, pageSize, sortBy } = pageParams
  const queryParams = {
    orderBy: sortBy,
    offset: (page - 1) * pageSize,
    limit: pageSize,
  }
  // search state
  const [nameState, handleSearchInputChange] = useState(null)
  const filterParam = nameState ? `name=="*${nameState}*"` : null
  const {
    state: { fields, pending, count },
    refresh,
  } = useFields({
    query: {
      ...queryParams,
      orderBy: stringifyOrderBy(sortBy),
      query: filterParam || undefined,
    },
    rtid: typeId,
  })
  return (
    <Box loose className={styles.FieldColumn}>
      <Box flex pl="2md" align="center" className={styles.ColumnHeader}>
        <Box loose pr="2md">
          <div data-testid={getTestAttribute(`add-button`)}>
            <PrimaryButton
              onClick={() =>
                setModalData({
                  id: `new`,
                  resourceTypeId: typeId,
                  name: ``,
                  label: ``,
                  type: `string`,
                  required: false,
                  index: count + 1,
                })
              }
              className={styles.AddButton}
              disabled={disableCreate}
            >
              <AddIcon size="small" color="white" />
            </PrimaryButton>
          </div>
        </Box>
        <Box loose className={styles.Rectangle} />
        <Box loose pl="2md">
          <div data-testid={getTestAttribute(`search-input`)}>
            <Input
              style={{ width: 330 }}
              placeholder="Введите наименование поля для поиска"
              icon={SearchIcon}
              onChange={({ target: { value } }) =>
                handleSearchInputChange(value)
              }
              value={nameState}
            />
          </div>
        </Box>
      </Box>
      <div data-testid={getTestAttribute(`table`)}>
        <DataGrid
          pending={pending}
          hideUselessPagination
          multiline
          showTextOverflowHintOn="hover"
          totalCount={count}
          withBorder
          pageSize={pageSize}
          className={styles.DataGrid}
          showPageSizeOptions
          onPageSizeChange={size =>
            setPageParams({ ...pageParams, pageSize: size, page: 1 })
          }
          onSortedChange={orderBy => {
            setPageParams({
              ...pageParams,
              sortBy: orderBy.length > 0 ? orderBy : undefined,
              page: 1,
            })
          }}
          lineTools={
            !disableEdit
              ? [
                  {
                    icon: `edit`,
                    onClick: ({ original }) => setModalData(original),
                  },
                  {
                    icon: `trash`,
                    onClick: ({ original }) => toggleDeletedField(original),
                  },
                ]
              : null
          }
          sorted={sortBy || []}
          manual
          data={fields}
          columns={columns}
          page={page - 1 || 0}
          onPageChange={page =>
            setPageParams({
              ...pageParams,
              page: page + 1,
            })
          }
          withSidesBorder
        />
      </div>
      {!isEmpty(modalData) && (
        <>
          <Box className={styles.FieldModal}>
            <CustomField
              data={modalData}
              close={() => {
                clear()
                setModalData({})
              }}
              fieldsCount={count}
              refreshGrid={refresh}
              setModalData={setModalData}
            />
          </Box>
          <Box
            loose
            className={cn(
              styles.ModalOverlay,
              areMenuCaptionsVisible && styles.OverlayWidth,
            )}
            onClick={() => setModalData({})}
          />
        </>
      )}
      <ModalConfirm
        isOpen={deletedField}
        title="Вы действительно хотите удалить поле?"
        buttonConf={[
          {
            text: `Удалить`,
            onClick: () => {
              remove({
                id: get(deletedField, `id`),
                name: get(deletedField, `name`),
                refresh,
              })
              toggleDeletedField(false)
            },
            isOk: true,
            done: true,
            disabled: false,
          },
          {
            text: `Отмена`,
            onClick: () => toggleDeletedField(false),
            disabled: false,
          },
        ]}
        isRemove
      >
        <div className={styles[`modal-message-wrapper`]}>
          <P className={styles[`modal-message-text`]}>
            {`"${get(deletedField, `name`)}"`}
          </P>
        </div>
      </ModalConfirm>
    </Box>
  )
}

GridFields.propTypes = {
  typeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  areMenuCaptionsVisible: PropTypes.bool,
  remove: PropTypes.func.isRequired,
}

GridFields.defaultProps = {
  areMenuCaptionsVisible: false,
}

export default connect(
  state => ({
    areMenuCaptionsVisible: get(state, `ui.areMenuCaptionsVisible`),
    disableCreate: !hasPermission(RESOURCE_TYPE_CREATE)(state),
    disableEdit: !hasPermission(RESOURCE_TYPE_UPDATE)(state),
  }),
  {
    remove,
    clear,
  },
)(GridFields)
